<script>
		import {user, view, change_attribute, send_data, fetch_data} from "../client.svelte.js"
		import {fieldResolution, filepath_URL, mapimg_URL} from "../core.js";
		import {onMount, tick} from "svelte";
		import Sidebar from "../elements/Sidebar.svelte";

		/// environment types: geometry to draw and how they are shown on the map (sizes in fields)
		const ENVIRONS = {
				Wall:    {label: "Mauer",   icon: "", geom: 'line', color: '#dddddd', width: 0.22},
				Fence:   {label: "Zaun",    icon: "", geom: 'line', color: '#999999', width: 0.12, dash: '0.35 0.18'},
				Curtain: {label: "Vorhang", icon: "", geom: 'line', color: '#a58ad6', width: 0.16, dash: '0.02 0.2', cap: 'round'},
				Door:    {label: "Tür",     icon: "", geom: 'line', max: 2, color: '#b9970d', width: 0.24},
				Portal:  {label: "Portal",  icon: "", geom: 'line', max: 2, color: '#7d0d89', width: 0.3, glow: true},
				Empty:   {label: "", icon: "", geom: '', },
				Water:   {label: "Wasser",  icon: "", geom: 'area', color: '#3D85C6', opacity: 0.3},
				Cliff:   {label: "Klippe",  icon: "", geom: 'area', color: '#867D7D', fill: '#514545', opacity: 0.3},
				Thicket: {label: "Dickicht", icon: "", geom: 'area', color: '#2cbcbc', fill: '#0D8989', opacity: 0.3},
				// Obscure: {label: "Klippe",  icon: "", geom: 'area', color: '#867D7D', fill: '#514545', opacity: 0.3},
				// Rugged:   {label: "Klippe",  icon: "", geom: 'area', color: '#867D7D', fill: '#514545', opacity: 0.3},
				Boulder: {label: "Findling",icon: "", geom: 'circle', color: '#F1C232', opacity: 0.25},
				Tree:    {label: "Baum",    icon: "", geom: 'circle', color: '#7aa84f', opacity: 0.25},
				Trap:    {label: "Falle",   icon: "", geom: 'circle', color: '#bf2828', opacity: 0.5},
		}
		const HINTS = {
				line:   "Klicke, um Punkte zu setzen. Doppelklick, Rechtsklick oder Enter beendet die Linie.",
				two:    "Klicke die beiden Endpunkte an.",
				area:   "Klicke die Ecken an. Ein Klick auf die erste Ecke, Doppelklick oder Enter schließt die Fläche.",
				circle: "Klicke auf den Stamm, bewege die Maus für die Größe der Krone und klicke erneut.",
		}
		const SNAPS = [["aus", 0], ["¼", 0.25], ["½", 0.5], ["1", 1]]
		const DOORSTATES = {open: "offen", closed: "geschlossen", locked: "verschlossen"}
		const DEPTHS = {shallow: "seicht", deep: "tief"}

		const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1])
		const round1 = (val) => Math.round(val * 10) / 10
		const points = (part) => part.map(pt => pt.join(",")).join(" ")
		const environ_spec = (obst) => ENVIRONS[obst.ZUB] ?? {label: obst.ZUB, icon: "", color: obst.color ?? '#ffffff', width: 0.15, opacity: 0.3}
		const environ_geom = (obst) => (obst.shape?.startsWith('POINT')) ? 'circle' : (obst.shape?.includes('POLYGON')) ? 'area' : 'line'
		const environ_name = (obst) => obst.params?.name ?? obst.ident
		const layer_name = (layer) => layer.name || ((layer.LAYER) ? `Ebene ${layer.LAYER}` : "Hauptebene")

		let pinchZoom = $state(), svgElem = $state(), newLayerInput = $state(), editDialog = $state(), layerDialog = $state()
		let tab = $state(null)          // open flyout: 'site' or 'environ'
		let layerID = $state(null)      // ID of the layer shown on the map
		let natural = $state({width: 0, height: 0})     // pixel size of the map image
		let zoom = $state(1)
		let snapIdx = $state(1)
		let showGrid = $state(true)
		let tool = $state(null)         // environment type that is currently drawn
		let draft = $state([])          // points of the environment element currently drawn
		let cursor = $state(null)       // field position of the mouse on the map
		let hoverID = $state(null)
		let edit = $state(null)         // copy of the environment element edited in dialog
		let doomedLayer = $state(null)  // layer to be deleted (after confirmation)
		let newIdent = $state("")

		let layers = $derived(Object.values(view.site?.layers ?? {}).map(layID => user.layers.get(layID)).filter(Boolean))
		let layer = $derived(layers.find(lay => lay.ID === layerID) ?? layers.find(lay => (lay.LAYER ?? "") === (view.site.main ?? "")) ?? layers[0])
		let environs = $derived(environs_of(layer))
		let mapCols = $derived((layer) ? natural.width / layer.scale : 0)		// size of the map in fields
		let mapRows = $derived((layer) ? natural.height / layer.scale : 0)
		let toolSpec = $derived(tool && ENVIRONS[tool])
		let closing = $derived(toolSpec?.geom === 'area' && draft.length >= 3 && cursor && dist(cursor, draft[0]) < 0.45)

		// $inspect(`SITE - edit`, edit)


		/// stop drawing when the site flyout is opened
		$effect(() => {
				if (tab === 'site') {
					tool = null
					draft = []
				}
		})

		/// load environment elements of all layers of this site
		$effect(() => {
				if (view.site?.ID)
					fetch_data('/environ', {SITE: view.site.ID})
		})

		/// fit map into view when map is shown, its size changes or the view is resized
		$effect(() => {
				if (!pinchZoom || !mapCols)  return
				const observer = new ResizeObserver(() => zoom_fit())
				observer.observe(pinchZoom)
				return () => observer.disconnect()
		})

		onMount(() => {
				window.addEventListener('keydown', process_key)
				return () => window.removeEventListener('keydown', process_key)
		})

		function environs_of(layer) {
				if (!layer)  return []
				return user.obstacles.values()
						.filter(obst => obst.SITE === layer.SITE && (obst.LAYER ?? "") === (layer.LAYER ?? ""))
						.toArray()
		}


		/// site and layers

		function select_layer(lay) {
				if (lay.ID === layer?.ID)  return
				layerID = lay.ID
				tool = null
				draft = []
				hoverID = null
		}

		function change_layer(lay, attrib, value) {
				if (attrib !== 'name')
					value = +value || null
				send_data(`/layer/${lay.ID}`, {[attrib]: value || null}, 'PATCH')
		}

		function propose_ident(name) {
				const useds = new Set(layers.map(lay => lay.LAYER ?? ""))
				const base = name.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 3) || "e"
				if (!useds.has(base))  return base
				for (let nr = 1; ; nr++)
					if (!useds.has(base.slice(0, 2) + nr))  return base.slice(0, 2) + nr
		}

		async function add_layer(e) {
				const mapfile = e.target.files[0]
				e.target.value = ""
				if (!mapfile)  return
				if (newIdent && !/^[a-z0-9]{1,3}$/.test(newIdent)) {
					alert("Das Kürzel der Ebene muss aus 1-3 Kleinbuchstaben oder Ziffern bestehen.")
					return
				}
				const name = mapfile.name.replace(/\.[^.]+$/, "")
				const ident = newIdent || propose_ident(name)
				if (layers.some(lay => lay.LAYER === ident)) {
					alert(`Die Ebene '${ident}' gibt es bereits.`)
					return
				}
				console.log(`SITE: Add layer ${ident} to site ${view.site.ID}`)
				await send_data(`/layer/${view.site.ID}_${ident}`, {SITE: view.site.ID, LAYER: ident, name}, 'POST', mapfile)
				newIdent = ""
				layerID = `${view.site.ID}_${ident}`
		}

		function ask_delete_layer(lay) {
				doomedLayer = lay
				layerDialog.showModal()
		}

		async function delete_layer() {
				const lay = doomedLayer
				layerDialog.close()
				if (lay.ID === layer?.ID)
					layerID = null
				await send_data(`/layer/${lay.ID}`, {}, 'DELETE')
		}


		/// map view

		function size_map(e) {
				natural = {width: e.target.naturalWidth, height: e.target.naturalHeight}
				tick().then(zoom_fit)
		}

		function zoom_by(factor) {
				const box = pinchZoom.getBoundingClientRect()
				const scale = pinchZoom.scale, newScale = Math.min(8, Math.max(0.05, scale * factor))
				const [midX, midY] = [box.width / 2, box.height / 2]
				pinchZoom.setTransform({scale: newScale, x: midX - (midX - pinchZoom.x) * newScale / scale, y: midY - (midY - pinchZoom.y) * newScale / scale})
		}

		function zoom_fit() {
				if (!pinchZoom || !mapCols)  return
				const box = pinchZoom.getBoundingClientRect()
				const [width, height] = [mapCols * fieldResolution, mapRows * fieldResolution]
				const scale = Math.min(box.width / width, box.height / height) * 0.95
				pinchZoom.setTransform({scale, x: (box.width - width * scale) / 2, y: (box.height - height * scale) / 2})
		}

		/** field position (col, row) of mouse event on map, snapped to grid */
		function to_field(e) {
				const rect = svgElem.getBoundingClientRect()
				let col = (e.clientX - rect.left) / rect.width * mapCols
				let row = (e.clientY - rect.top) / rect.height * mapRows
				const snap = SNAPS[snapIdx][1]
				if (snap) {
					col = Math.round(col / snap) * snap
					row = Math.round(row / snap) * snap
				}
				return [Math.max(0, Math.min(mapCols, round1(col))), Math.max(0, Math.min(mapRows, round1(row)))]
		}


		/// drawing of environment elements

		function select_tool(type) {
				tool = (tool === type) ? null : type
				draft = []
		}

		function process_mapClick(e) {
				if (!tool)  return
				const pos = to_field(e)
				if (toolSpec.geom === 'circle') {
					if (!draft.length)
						draft = [pos]
					else
						commit(tool, [draft[0]], Math.max(0.5, dist(draft[0], pos)))
					return
				}
				if (toolSpec.geom === 'area' && draft.length >= 3 && dist(pos, draft[0]) < 0.45)
					return finish()
				const nextDraft = [...draft, pos]
				if (toolSpec.max && nextDraft.length >= toolSpec.max)
					return finish(nextDraft)
				draft = nextDraft
		}

		function finish(pts = draft) {
				if (!tool)  return
				pts = pts.filter((pt, i) => i === 0 || dist(pt, pts[i - 1]) > 0.01)		// remove double points (e.g. from double-click)
				if (toolSpec.geom === 'circle' || (toolSpec.geom === 'line' && pts.length < 2) || (toolSpec.geom === 'area' && pts.length < 3)) {
					if (!pts.length)  tool = null
					return
				}
				commit(tool, pts)
		}

		async function commit(type, coords, radius = null) {
				draft = []
				const params = (radius) ? {radius: round1(radius)} : {}
				await send_data('/environ', {SITE: layer.SITE, LAYER: layer.LAYER ?? "", type, coords, params})
		}

		function process_key(e) {
				if (editDialog?.open || layerDialog?.open || ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName))  return
				if (e.key === 'Escape') {
					if (draft.length)  draft = []
					else  tool = null
				} else if (e.key === 'Enter' && tool) {
					e.preventDefault()
					finish()
				} else if (e.key === 'Backspace' && draft.length) {
					e.preventDefault()
					draft = draft.slice(0, -1)
				}
		}


		/// editing of environment elements

		function open_edit(obst) {
				hoverID = null
				edit = {ID: obst.ID, ident: obst.ident, ZUB: obst.ZUB, geom: environ_geom(obst), layer: obst.LAYER ?? "", params: Object.assign({}, obst.params),
								lookthrough: obst.lookthrough, walkthrough: obst.walkthrough, requires: obst.requires}
				edit.params.name ??= obst.ident
				edit.params.radius ??= (edit.geom === 'circle') ? 1 : undefined
				editDialog.showModal()
		}

		async function save_edit() {
				const params = Object.assign({}, edit.params)
				params.name = (params.name ?? "").replace(/[,:{}\[\]#='"]/g, "").trim()     // characters not allowed in stored parameter strings
				if (params.name === edit.ident)
					delete params.name
				editDialog.close()
				await send_data(`/environ/${encodeURIComponent(edit.ID)}`, {type: edit.ZUB, params}, 'PATCH')
		}

		async function delete_edit() {
				editDialog.close()
				await send_data(`/environ/${encodeURIComponent(edit.ID)}`, {}, 'DELETE')
		}

</script>


<!------------------------------- HTML ------------------------------------->

{#snippet panel(tab)}
		{#if tab === 'site'}
			<div class="fields">
				<label for=name>Name</label>
				<input id=name class=text type=text value={view.site.name} onchange={change_attribute.bind(view.site)}/>
				<label for=description>Kurzbeschreibung</label>
				<textarea id=description class=text rows=4 onchange={change_attribute.bind(view.site)}>{view.site.description ?? ""}</textarea>
				<div class="tags">
					<span class=tag>Eigentümer: {view.site.owner}</span>
					<span class=tag>Kategorie: {view.site.rubric ?? "–"}</span>
				</div>
			</div>

			<h3>Ebenen</h3>
			<div class="layerList" data-help="layer">
				{#each layers as lay (lay.ID)}
					<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
					<div class="layerRow" class:selected={lay.ID === layer?.ID} onclick={() => select_layer(lay)}>
						<img class=thumb src={mapimg_URL({owner: view.site.owner, ID: lay.ID})} alt={lay.LAYER}
								 onerror={(e) => {e.target.onerror = null; e.target.src = filepath_URL(lay.filepath)}}/>
						<div class="layerData">
							<input class=text type=text value={layer_name(lay)} onchange={(e) => change_layer(lay, 'name', e.target.value)}
										 onfocus={(e) => e.target.select()}/>
							<span class=meta>{lay.LAYER || "–"} · {environs_of(lay).length} Elemente</span>
						</div>
						<button class="delete button-circle icon-trash" title="Ebene löschen" disabled={layers.length < 2}
										onclick={(e) => {e.stopPropagation(); ask_delete_layer(lay)}}></button>
					</div>
				{/each}
			</div>
			{#if layer}
				<div class="layerParams">
					<label for=scale>Pixel/Feld</label>
					<input id=scale class=text type=number min=10 value={layer.scale} onchange={(e) => change_layer(layer, 'scale', e.target.value)}/>
					<label for=visib>Sichtweite</label>
					<input id=visib class=text type=number min=1 value={layer.visib} onchange={(e) => change_layer(layer, 'visib', e.target.value)}/>
				</div>
			{/if}
			<div class="newLayer" data-help="newLayer">
				<input class=text type=text placeholder="Kürzel" maxlength=3 bind:value={newIdent} title="Kürzel der neuen Ebene (1-3 Kleinbuchstaben), wird sonst aus dem Dateinamen gebildet"/>
				<button class="btn-primary" onclick={() => newLayerInput.click()}>
					<i class="icon">{""}</i> Ebene hinzufügen
				</button>
				<input bind:this={newLayerInput} type=file accept=".jpg, .jpeg, .webp, .png" onchange={add_layer} style="display: none"/>
			</div>

		{:else}
			<h3>Umgebungsarten</h3>
			<div class="tiles" data-help="environ">
				{#each Object.entries(ENVIRONS) as [type, spec]}
					<button class="tile" class:selected={tool === type} title="{spec.label} zeichnen"
					        disabled={type==='Empty'} onclick={() => select_tool(type)}>
						<i class="icon" style:color={spec.color}>{spec.icon}</i>
						<span>{spec.label}</span>
					</button>
				{/each}
			</div>
			{#if tool}
				<div class="drawHint">
					<span class=kicker>{toolSpec.label} zeichnen</span>
					<span>{(toolSpec.max === 2) ? HINTS.two : HINTS[toolSpec.geom]}</span>
					<span class=meta>Rücktaste entfernt den letzten Punkt · Esc bricht ab · Mausrad verschiebt die Karte</span>
				</div>
			{/if}

			<h3>Auf {(layer) ? layer_name(layer) : "–"} · {environs.length}</h3>
			{#if !environs.length}
				<p class=meta>Auf dieser Ebene ist noch nichts eingezeichnet. Wähle oben eine Umgebungsart und zeichne sie auf die Karte.</p>
			{/if}
			<div class="environList" data-help="environs">
				{#each environs as obst (obst.ID)}
					{@const spec = environ_spec(obst)}
					<button class:hovered={hoverID === obst.ID} onclick={() => open_edit(obst)}
									onmouseenter={() => hoverID = obst.ID} onmouseleave={() => hoverID = null}>
						<i class="icon" style:color={spec.color}>{spec.icon}</i>
						<span class=environName>{environ_name(obst)}</span>
						<span class=meta>{spec.label}</span>
					</button>
				{/each}
			</div>
		{/if}
{/snippet}

<section id={view.layout.page}>
	<Sidebar bind:open={tab} flyout={panel} width={340} items={[
		{id: 'site', help: 'layer', icon: 'layer-group', label: "Ort & Ebenen", flyout: "Ort & Ebenen"},
		{id: 'environ', icon: 'tree', label: "Umgebung", flyout: "Umgebung", active: !!tool} ]}/>

	<article class="mapPane">
		{#if layer}
			<pinch-zoom bind:this={pinchZoom} class="pinchable" class:drawing={tool} onchange={() => zoom = pinchZoom.scale}>
				<img class="pinchable" src={filepath_URL(layer.filepath)} alt="" draggable=false onload={size_map}
						 style:width="{mapCols * fieldResolution}px" />
				{#if mapCols}
					<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
					<svg bind:this={svgElem} class:pinchable={!tool} viewBox="0 0 {mapCols} {mapRows}"
							 style:width="{mapCols * fieldResolution}px" style:height="{mapRows * fieldResolution}px"
							 onpointermove={(e) => cursor = to_field(e)} onpointerleave={() => cursor = null} onclick={process_mapClick}
							 ondblclick={(e) => {if (tool) {e.preventDefault(); finish()}}}
							 oncontextmenu={(e) => {if (tool) {e.preventDefault(); finish()}}}>
						<defs>
							<pattern id="envGrid" width=1 height=1 patternUnits="userSpaceOnUse">
								<path d="M1 0H0V1" fill="none" stroke="#dddddd" stroke-opacity=0.2 stroke-width=0.03 />
							</pattern>
							<filter id="envGlow" filterUnits="userSpaceOnUse" x=-1 y=-1 width={mapCols + 2} height={mapRows + 2}>
								<feGaussianBlur stdDeviation=0.18 result="blur" />
								<feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
							</filter>
						</defs>

						{#snippet figure(spec, geom, parts, radius, hovered, drafted)}
							{#if geom === 'circle'}
								{@const [cx, cy] = parts[0][0]}
								{#if hovered}<circle class=outline {cx} {cy} r={radius + 0.12} stroke-width=0.2 />{/if}
								<circle {cx} {cy} r={radius} fill={spec.color} fill-opacity={spec.opacity} stroke={spec.color} stroke-width=0.08 />
								<circle {cx} {cy} r=0.14 fill={spec.color} />
							{:else if geom === 'area'}
								{#each parts as part}
									{#if hovered}<polygon class=outline points={points(part)} stroke-width=0.32 />{/if}
									{#if drafted}
										<polyline points={points(part)} fill="none" stroke={spec.color} stroke-width=0.08 />
									{:else}
										<polygon points={points(part)} fill={spec.fill ?? spec.color} fill-opacity={spec.opacity} stroke={spec.color} stroke-width=0.08 stroke-linejoin="round" />
									{/if}
								{/each}
							{:else}
								{#each parts as part}
									{#if hovered}<polyline class=outline points={points(part)} stroke-width={spec.width + 0.32} />{/if}
									{#if spec.ticks}<polyline points={points(part)} fill="none" stroke={spec.color} stroke-width=0.5 stroke-dasharray="0.06 0.22" />{/if}
									<polyline points={points(part)} fill="none" stroke={spec.color} stroke-width={spec.width} stroke-dasharray={spec.dash}
														stroke-linecap={spec.cap ?? ((spec.dash) ? 'butt' : 'square')} stroke-linejoin="round" filter={(spec.glow) ? "url(#envGlow)" : null} />
									{#if spec === ENVIRONS.Door && part.length === 2}
										{#each part as [x, y]}<rect x={x - 0.12} y={y - 0.12} width=0.24 height=0.24 fill="#dddddd" />{/each}
									{/if}
									{#if !drafted}<polyline class=hitArea points={points(part)} />{/if}
								{/each}
							{/if}
						{/snippet}

						{#if showGrid}<rect class=grid width={mapCols} height={mapRows} fill="url(#envGrid)" />{/if}

						{#each environs as obst (obst.ID)}
							{@const geom = environ_geom(obst)}
							<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
							<g class="environ" class:inactive={tool} onclick={(e) => {e.stopPropagation(); open_edit(obst)}}
								 onmouseenter={() => hoverID = obst.ID} onmouseleave={() => hoverID = null}>
								{@render figure(environ_spec(obst), geom, obst.coords ?? [], +(obst.params?.radius ?? 1), hoverID === obst.ID, false)}
							</g>
						{/each}

						{#if tool && draft.length}
							<g class="draft">
								{#if toolSpec.geom === 'circle'}
									{@render figure(toolSpec, 'circle', [draft], (cursor) ? Math.max(0.5, dist(draft[0], cursor)) : 1, false, true)}
								{:else}
									{@render figure(toolSpec, toolSpec.geom, [draft], 1, false, true)}
									{#if cursor}
										{@const last = draft.at(-1)}
										<line class=rubberband x1={last[0]} y1={last[1]} x2={cursor[0]} y2={cursor[1]} />
										{#if toolSpec.geom === 'area' && draft.length >= 2}
											<line class="rubberband closing" x1={cursor[0]} y1={cursor[1]} x2={draft[0][0]} y2={draft[0][1]} />
										{/if}
									{/if}
									{#each draft as [cx, cy], i}
										<circle class=vertex {cx} {cy} r={(i === 0 && closing) ? 0.26 : 0.13} />
									{/each}
								{/if}
							</g>
						{/if}
						{#if tool && cursor}<circle class=cursorDot cx={cursor[0]} cy={cursor[1]} r=0.09 />{/if}
					</svg>
				{/if}
			</pinch-zoom>

			<nav class="floating layerPills">
				{#each layers as lay (lay.ID)}
					<button class:selected={lay.ID === layer.ID} onclick={() => select_layer(lay)}>{layer_name(lay)}</button>
				{/each}
			</nav>

			<div class="floating mapTools">
				<button class:active={!tool} title="Auswählen & bearbeiten (Esc)" onclick={() => {tool = null; draft = []}}><i class=icon>{""}</i></button>
				<span class=divider></span>
				<button class:active={SNAPS[snapIdx][1]} title="Am Raster ausrichten: {SNAPS[snapIdx][0]}" onclick={() => snapIdx = (snapIdx + 1) % SNAPS.length}>
					<i class=icon>{""}</i> {SNAPS[snapIdx][0]}
				</button>
				<button class:active={showGrid} title="Raster anzeigen" onclick={() => showGrid = !showGrid}><i class=icon>{""}</i></button>
				<span class=divider></span>
				<button title="Verkleinern" onclick={() => zoom_by(1 / 1.25)}><i class=icon>{""}</i></button>
				<button class=zoomLabel title="Einpassen" onclick={zoom_fit}>{Math.round(zoom * 100)}%</button>
				<button title="Vergrößern" onclick={() => zoom_by(1.25)}><i class=icon>{""}</i></button>
			</div>

			<div class="floating statusBar">
				<span class=position>Feld {(cursor) ? `${cursor[0].toFixed(1)} / ${cursor[1].toFixed(1)}` : "– / –"}</span>
				<span class=meta>
					{(tool) ? `${toolSpec.label} zeichnen${(draft.length) ? ` · ${draft.length} Punkt${(draft.length > 1) ? "e" : ""}` : ""}` : "Klicke auf ein Element, um es zu bearbeiten"}
				</span>
			</div>
		{:else}
			<p class="noMap meta">Dieser Ort hat noch keine Ebene. Füge eine Ebene aus einem Kartenbild hinzu.</p>
		{/if}
	</article>
</section>


<!-- dialog to edit an environment element -->
<dialog bind:this={editDialog} class="siteDialog" onclose={() => edit = null}
				onclick={(e) => {if (e.target === editDialog) editDialog.close()}}>
	{#if edit}
		{@const spec = ENVIRONS[edit.ZUB] ?? environ_spec(edit)}
		<div class="dialogHead">
			<i class="icon" style:color={spec.color}>{spec.icon}</i>
			<h3>{spec.label} bearbeiten</h3>
			<button class="close button-circle icon-xmark" title="Schließen" onclick={() => editDialog.close()}></button>
		</div>
		<div class="dialogGrid">
			<div class="field">
				<label for=envName>Name</label>
				<input id=envName class=text type=text bind:value={edit.params.name} />
			</div>
			<div class="field">
				<label for=envType>Art</label>
				<select id=envType class=text bind:value={edit.ZUB}>
					{#each Object.entries(ENVIRONS).filter(([type, typeSpec]) => typeSpec.geom === edit.geom || type === edit.ZUB) as [type, typeSpec]}
						<option value={type}>{typeSpec.label}</option>
					{/each}
					{#if !(edit.ZUB in ENVIRONS)}<option value={edit.ZUB}>{edit.ZUB}</option>{/if}
				</select>
			</div>
		</div>
		<div class="checks">
			<label data-help="walkthrough">
				<input type=checkbox checked={(edit.params.walkthrough ?? edit.walkthrough) !== true}
							 onchange={(e) => edit.params.walkthrough = !e.target.checked} />
				Blockiert Bewegung
				{#if edit.walkthrough == null && edit.params.walkthrough == null && edit.requires}<span class=meta>(außer mit {edit.requires})</span>{/if}
			</label>
			<label data-help="lookthrough">
				<input type=checkbox checked={!(edit.params.lookthrough ?? edit.lookthrough)}
							 onchange={(e) => edit.params.lookthrough = !e.target.checked} />
				Blockiert Sicht
			</label>
		</div>
		{#if edit.ZUB === 'Door'}
			<div class="field" data-help="doorstate">
				<span class=label>Zustand</span>
				<div class="segments">
					{#each Object.entries(DOORSTATES) as [state, label]}
						<button class:selected={(edit.params.state ?? 'closed') === state} onclick={() => edit.params.state = state}>{label}</button>
					{/each}
				</div>
			</div>
		{:else if edit.ZUB === 'Water'}
			<div class="field" data-help="depth">
				<span class=label>Tiefe</span>
				<div class="segments">
					{#each Object.entries(DEPTHS) as [depth, label]}
						<button class:selected={(edit.params.depth ?? 'shallow') === depth} onclick={() => edit.params.depth = depth}>{label}</button>
					{/each}
				</div>
			</div>
		{:else if edit.ZUB === 'Portal'}
			<div class="field">
				<label for=envMap>Führt zur Ebene</label>
				<select id=envMap class=text bind:value={edit.params.map}>
					<option value={undefined}>–</option>
					{#each layers.filter(lay => (lay.LAYER ?? "") !== edit.layer) as lay}
						<option value={lay.LAYER}>{layer_name(lay)}</option>
					{/each}
				</select>
			</div>
		{:else if edit.ZUB === 'Cliff'}
			<div class="field narrow">
				<label for=envHeight>Höhe (m)</label>
				<input id=envHeight class=text type=number min=1 bind:value={edit.params.height} />
			</div>
		{:else if edit.geom === 'circle'}
			<div class="field narrow">
				<label for=envRadius>Radius (Felder)</label>
				<input id=envRadius class=text type=number min=0.5 step=0.5 bind:value={edit.params.radius} />
			</div>
		{/if}
		<div class="dialogActions">
			<button class="btn-primary neutral" onclick={delete_edit}><i class=icon>{""}</i> Löschen</button>
			<span></span>
			<button class="btn-primary neutral" onclick={() => editDialog.close()}>Abbrechen</button>
			<button class="btn-primary" onclick={save_edit}>Speichern</button>
		</div>
	{/if}
</dialog>

<!-- confirmation to delete a layer -->
<dialog bind:this={layerDialog} class="siteDialog" onclose={() => doomedLayer = null}>
	{#if doomedLayer}
		<h3>Ebene „{layer_name(doomedLayer)}“ löschen?</h3>
		<p>Die Ebene und ihre {environs_of(doomedLayer).length} Umgebungselemente werden entfernt.</p>
		<div class="dialogActions">
			<span></span>
			<button class="btn-primary neutral" onclick={() => layerDialog.close()}>Abbrechen</button>
			<button class="btn-primary" onclick={delete_layer}>Ebene löschen</button>
		</div>
	{/if}
</dialog>


<!------------------------------- STYLE ------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	.icon
		font-family: "Font Awesome 6 Free"
		font-weight: 400
		font-style: normal

	.kicker
		font-size: 12px
		text-transform: uppercase
		letter-spacing: 0.08em
		color: colors.$primary-100

	.meta
		font-size: 12px
		color: colors.$grey-60

	h3
		margin: 20px 0 8px 0
		font-size: 14px
		font-weight: 500
		color: colors.$grey-60

	/** flyouts next to the sidebar **/
	.segments
		display: flex
		align-self: flex-start
		margin-top: 12px
		padding: 3px
		gap: 3px
		border-radius: 8px
		background: colors.$grey-15
		box-shadow: inset 0 0 0 1px colors.$grey-30

	.segments button
		display: flex
		align-items: center
		gap: 6px
		padding: 5px 12px
		border: 0
		border-radius: 6px
		background: transparent
		color: colors.$grey-85
		font: inherit
		font-size: 13px
		white-space: nowrap
		cursor: pointer

	.segments button:hover
		background: colors.$primary-020

	.segments button.selected
		background: colors.$primary-060-black
		color: colors.$white

	.fields
		display: flex
		flex-direction: column
		gap: 4px
		margin-top: 0

	.fields label, .field label, .field .label, .layerParams label
		font-size: 13px
		color: colors.$grey-60

	.fields label:not(:first-child)
		margin-top: 8px

	input.text, textarea.text, select.text
		height: auto
		padding: 6px 8px
		background: colors.$grey-15
		border: 1px solid colors.$grey-30
		border-radius: 6px
		color: colors.$white
		font: inherit
		font-size: 14px

	input.text:focus, textarea.text:focus, select.text:focus
		outline: none
		border-color: colors.$primary-100

	textarea.text
		resize: vertical

	.tags
		display: flex
		flex-wrap: wrap
		gap: 8px
		margin-top: 12px

	.tag
		padding: 2px 10px
		border-radius: 12px
		background: colors.$grey-20
		font-size: 12px
		color: colors.$grey-85

	.layerList
		display: flex
		flex-direction: column
		gap: 4px

	.layerRow
		display: flex
		align-items: center
		gap: 10px
		padding: 6px
		border: 1px solid transparent
		border-radius: 8px
		cursor: pointer

	.layerRow:hover
		background: colors.$primary-020

	.layerRow.selected
		border-color: colors.$primary-100
		background: colors.$primary-040-black

	.layerRow .thumb
		flex: none
		width: 56px
		height: 40px
		object-fit: cover
		border-radius: 4px
		background: colors.$grey-15

	.layerData
		flex: 1
		min-width: 0
		display: flex
		flex-direction: column
		gap: 2px

	.layerData input.text
		padding: 3px 8px
		font-size: 13px

	.layerRow .delete
		flex: none
		width: 30px
		height: 30px
		font-size: 13px

	.layerRow .delete:disabled
		opacity: 0.3
		cursor: default

	.layerParams
		display: grid
		grid-template-columns: auto 8ch auto 8ch
		align-items: center
		gap: 8px
		margin-top: 12px

	.newLayer
		display: flex
		align-items: center
		gap: 8px
		margin-top: 16px

	.newLayer input.text
		width: 6ch

	.newLayer .btn-primary
		flex-flow: row
		gap: 6px

	.tiles
		display: grid
		grid-template-columns: repeat(3, minmax(0, 1fr))
		gap: 6px

	.tile
		display: flex
		flex-direction: column
		align-items: center
		gap: 6px
		padding: 14px 4px 10px
		border: 1px solid colors.$grey-30
		border-radius: 8px
		background: transparent
		color: colors.$white
		font: inherit
		font-size: 12px
		cursor: pointer

	.tile .icon
		font-size: 22px

	.tile:hover
		background: colors.$primary-020

	.tile.selected
		border-color: colors.$primary-100
		background: colors.$primary-040-black

	.drawHint
		display: flex
		flex-direction: column
		gap: 4px
		margin-top: 12px
		padding: 12px
		border-radius: 8px
		background: colors.$grey-15
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5)
		font-size: 13px

	.environList
		display: flex
		flex-direction: column

	.environList button
		display: flex
		align-items: center
		gap: 10px
		padding: 6px 10px
		border: 0
		border-radius: 6px
		background: transparent
		color: colors.$white
		font: inherit
		font-size: 13px
		text-align: left
		cursor: pointer

	.environList button:hover, .environList button.hovered
		background: colors.$primary-020

	.environName
		flex: 1
		min-width: 0
		overflow: hidden
		text-overflow: ellipsis
		white-space: nowrap

	/** map **/
	.mapPane
		overflow: hidden
		background: radial-gradient(ellipse at 30% 20%, colors.$grey-20, colors.$black 70%)

	pinch-zoom
		position: absolute
		inset: 0
		cursor: grab

	pinch-zoom.drawing svg
		cursor: crosshair

	pinch-zoom img
		display: block
		max-width: none
		user-select: none
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6)

	pinch-zoom svg
		position: absolute
		left: 0
		top: 0
		user-select: none

	svg .grid
		pointer-events: none

	svg .environ
		cursor: pointer

	svg .environ.inactive, svg .draft, svg .cursorDot
		pointer-events: none

	svg .outline
		fill: none
		stroke: colors.$primary-100
		stroke-opacity: 0.7
		stroke-linejoin: round
		stroke-linecap: round

	svg .hitArea
		fill: none
		stroke: transparent
		stroke-width: 0.8

	svg .rubberband
		stroke: colors.$primary-100
		stroke-width: 0.06
		stroke-dasharray: 0.2 0.14
		pointer-events: none

	svg .rubberband.closing
		stroke-opacity: 0.4

	svg .vertex
		fill: colors.$black
		stroke: colors.$primary-100
		stroke-width: 0.05

	svg .cursorDot
		fill: colors.$primary-100

	.floating
		position: absolute
		z-index: 2
		display: flex
		align-items: center
		gap: 4px
		padding: 4px
		border-radius: 8px
		background: colors.$grey-15
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6)

	.floating button
		display: flex
		align-items: center
		gap: 4px
		height: 30px
		min-width: 30px
		justify-content: center
		padding: 0 8px
		border: 0
		border-radius: 6px
		background: transparent
		color: colors.$grey-85
		font: inherit
		font-size: 13px
		cursor: pointer

	.floating button:hover
		background: colors.$primary-020

	.floating button.selected
		background: colors.$primary-060-black
		color: colors.$white

	.floating button.active
		color: colors.$primary-100

	.layerPills
		top: 12px
		left: 12px

	.mapTools
		top: 12px
		right: 12px

	.mapTools .zoomLabel
		min-width: 56px
		font-size: 12px

	.divider
		width: 1px
		height: 20px
		background: colors.$grey-30

	.statusBar
		bottom: 12px
		left: 12px
		gap: 16px
		padding: 6px 14px
		font-size: 12px

	.position
		font-variant-numeric: tabular-nums

	.noMap
		margin: 40px
		text-align: center

	/** dialogs **/
	.siteDialog
		width: min(420px, 90vw)
		padding: 20px
		background: colors.$grey-15
		color: colors.$white
		border: 1px solid colors.$grey-30
		border-radius: 12px
		box-shadow: 0 8px 32px rgba(0, 0, 0, 0.7)

	.siteDialog::backdrop
		background: rgba(0, 0, 0, 0.5)

	.siteDialog h3
		margin: 0
		color: colors.$white
		font-size: 17px

	.dialogHead
		display: flex
		align-items: center
		gap: 12px
		margin-bottom: 16px

	.dialogHead .icon
		font-size: 22px

	.dialogHead h3
		flex: 1

	.dialogHead .close
		width: 30px
		height: 30px

	.dialogGrid
		display: grid
		grid-template-columns: minmax(0, 1fr) 140px
		gap: 12px

	.field
		display: flex
		flex-direction: column
		gap: 4px
		margin-top: 12px

	.dialogGrid .field
		margin-top: 0

	.field.narrow
		max-width: 140px

	.field .segments
		margin-top: 0

	.checks
		display: flex
		flex-direction: column
		gap: 8px
		margin-top: 16px
		font-size: 14px

	.checks label
		display: flex
		align-items: center
		gap: 10px
		cursor: pointer

	.checks input
		width: 16px
		height: 16px
		accent-color: colors.$primary-100

	.dialogActions
		display: grid
		grid-template-columns: auto 1fr auto auto
		gap: 8px
		margin-top: 24px

	.dialogActions .btn-primary
		flex-flow: row
		gap: 6px
		min-width: 0
		padding: 8px 14px
</style>
